// src/landing/LandingLesvita.jsx
//
// Landing NOVA do lesvita.com (lançamento web) — porte pixel-fiel do design
// LesVita-Landing.dc.html (projeto "LesVita Landing e Fluxo" do Claude Design).
// Substitui a landing antiga na rota "/" (web). Mobile-first; a animação do
// celular do hero roda em loop automático, sem interação.
//
// Estilos em styles/landing/landingNova.scss (classes globais `lv*`). O JSX
// não carrega estilo inline: o que varia por estado vira classe alternada.
//
// Preço exibido: âncora R$ 59,90 (display) → R$ 29,90/mês, badge 50% OFF —
// decisão do lançamento; o cobrado de verdade vem do backend no fluxo /assinar.
//
// DOIS MODOS DE CTA (`landing/modoDoCta.js`), trocáveis pelo painel em
// `APP_CONFIG/LANDING_CTA` sem deploy:
//
//   APP (padrão)  os três botões dizem "Começar agora" e vão DIRETO para a loja
//                 do aparelho — App Store no iPhone, Play Store no Android,
//                 `/baixar` no desktop.
//   FORMS         a landing como era: `#plano` e depois `/assinar`.
//
// O modo NÃO muda nada além do destino e do rótulo dos CTAs: a seção de plano,
// o preço e o cartão free continuam iguais nos dois, porque quem chega pela
// busca precisa ver o que está comprando de qualquer forma.

import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "../components/layout/Seo.jsx";
import { seo } from "../data/seoData";
import { abrirApp } from "../utils/abrirApp";
import { ctaDaLanding } from "./modoDoCta";
import useModoDoCta from "./useModoDoCta";
import "../styles/landing/landingNova.css";

const ICONE = "/landing/icone-app.png";
const AVATAR = "/landing/avatar-vita.webp";
const PRATO = "/landing/prato.avif";

// ── Chat do hero (mesmo roteiro/timings do design) ──────────────────────────
const AV = <img src={AVATAR} alt="VITA" className="lvMsgAvatar" />;
const B = (t) => <b className="lvBubbleStrong">{t}</b>;
const rowV = (body, key) => (
  <div key={key} className="lvMsgRow">
    {AV}
    <div className="lvBubbleVita">{body}</div>
  </div>
);
const rowU = (body, key, pad) => (
  <div key={key} className="lvMsgRowUser">
    <div className={`lvBubbleUser${pad ? " lvBubblePad" : ""}`}>{body}</div>
  </div>
);
const pills = (arr, key) => (
  <div key={key} className="lvPills">
    {arr.map((t, i) => <span key={i} className="lvPill">{t}</span>)}
  </div>
);
const CHAT_ITEMS = [
  rowU(["Bom dia VITA, o que tenho hoje?"], "u1"),
  rowV([<React.Fragment key="f">Bom dia! 💪 Seu dia já está pronto: {B("Treino B — Costas e Bíceps")} e sua dieta de {B("2.180 kcal")} te esperando.</React.Fragment>], "v1"),
  pills(["Treino de hoje", "Minha dieta"], "p1"),
  rowU([<img key="foto" src={PRATO} alt="Foto da refeição" className="lvChatPhoto" />], "u2", true),
  rowV([<React.Fragment key="f">Escaneei sua refeição ✅ {B("Frango + arroz + salada — 520 kcal · 42g de proteína.")} Já lancei no seu diário. Faltam {B("68g de proteína")} pra bater a meta de hoje 👊</React.Fragment>], "v2"),
  pills(["Diário alimentar", "Score VITA"], "p2"),
];

// [tempo ms, patch de estado] — roteiro idêntico ao design (chatSpeed 1x).
const TIMELINE = [
  [400, { notif: true }],
  [3400, { notif: false }],
  [4000, { shown: 1 }],
  [4800, { typing: true }],
  [6500, { typing: false, shown: 2 }],
  [7400, { shown: 3 }],
  [8600, { shown: 4 }],
  [9500, { typing: true }],
  [11400, { typing: false, shown: 5 }],
  [12400, { shown: 6 }],
  [15600, { faded: true }],
  [16400, { shown: 0, typing: false, notif: false, faded: false }],
];
const LOOP_MS = 16900;

// Ícones: só viewBox/paths no JSX — tamanho, traço e cor vêm de .lvToolIcon svg.
const FERRAMENTAS = [
  { d: true, t: "VITA no WhatsApp", s: "Seu personal de IA particular, 24h no seu chat.", svg: <svg viewBox="0 0 24 24"><circle cx="12" cy="11" r="8" /><path d="M7 20l2.5-2.5" /></svg> },
  { t: "Scan de refeição", s: "Foto do prato → calorias e macros na hora.", svg: <svg viewBox="0 0 24 24"><rect x="4" y="6" width="16" height="13" rx="3" /><line x1="2" y1="12.5" x2="22" y2="12.5" /></svg> },
  { t: "Análise corporal com IA", s: "Análise corporal completa a partir de fotos guiadas.", svg: <svg viewBox="0 0 24 24"><circle cx="12" cy="6" r="3" /><rect x="8" y="11.5" width="8" height="9" rx="4" /></svg> },
  { t: "Treino 100% personalizado", s: "Montado pela IA para o seu corpo e objetivo.", svg: <svg viewBox="0 0 24 24"><line x1="8" y1="12" x2="16" y2="12" /><rect x="4" y="8" width="3" height="8" rx="1" /><rect x="17" y="8" width="3" height="8" rx="1" /></svg> },
  { t: "Dieta 100% personalizada", s: "Cardápio completo, ajustado à sua rotina.", svg: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" /></svg> },
  { t: "Gamificação que vicia", s: "XP em dobro, ofensivas, rankings e Score VITA.", svg: <svg viewBox="0 0 24 24"><rect x="7.5" y="7.5" width="9" height="9" rx="1.5" transform="rotate(45 12 12)" /></svg> },
  { t: "Evolução inteligente", s: "A IA acompanha e ajusta seu programa de objetivo.", svg: <svg viewBox="0 0 24 24" strokeLinejoin="round"><polyline points="4 17 10 11 14 14 20 7" /></svg> },
  { t: "Rotina fitness organizada", s: "Tudo num lugar só — treino, dieta e progresso.", svg: <svg viewBox="0 0 24 24"><line x1="5" y1="7" x2="19" y2="7" /><line x1="5" y1="12" x2="19" y2="12" /><line x1="5" y1="17" x2="13" y2="17" /></svg> },
];

const PASSOS = [
  { n: "01", t: "Assine e crie sua conta em 2 minutos", s: "Cadastro rápido, sem burocracia." },
  { n: "02", t: "O VITA monta tudo para você", s: "Seu treino, sua dieta e sua análise corporal — personalizados pela IA." },
  { n: "03", t: "Evolua pelo WhatsApp e pelo app", s: "Ele registra tudo por você — é só treinar e responder no chat." },
];

const INCLUSO = [
  <React.Fragment key="0"><strong>VITA no WhatsApp</strong> — seu personal de IA particular</React.Fragment>,
  "Scan de refeição por foto",
  "Análise corporal completa com IA",
  "Treino completo personalizado",
  "Dieta completa personalizada",
  "Registro de treinos ilimitado",
  "VITA no app ilimitado",
  "XP em dobro (x2) + 3 congelamentos de ofensiva por semana",
  "Evolução inteligente + programa de objetivo",
  "Atualizações antecipadas",
];
const INCLUSO_EXTRA = [
  "Rankings e ligas semanais",
  "Histórico completo de análises corporais",
  "Novos recursos primeiro, antes de todo mundo",
];

const FAQ = [
  { q: "Preciso do app pra usar o VITA?", a: "Não. O VITA funciona direto no seu WhatsApp — treino, dieta, scan e registro, tudo no chat. O app complementa com gráficos, análise corporal guiada e rankings." },
  { q: "Como funciona a garantia de 7 dias?", a: "Assinou e não curtiu? Em até 7 dias você pede o reembolso total, sem perguntas. É só chamar o suporte." },
  { q: "O VITA substitui um profissional?", a: "Não — ele potencializa o cuidado. O VITA organiza treino, dieta e evolução com base em ciência, e funciona ainda melhor junto do acompanhamento do seu médico, nutri ou personal." },
  { q: "Posso cancelar quando quiser?", a: "Sim. Cancelamento livre, direto no app ou pelo suporte — sem multa e sem burocracia." },
  { q: "Como funciona o pagamento?", a: "Pagamento 100% seguro: cartão de crédito em 12x de R$ 29,90 (total R$ 358,80 no ano) ou PIX à vista com 5% de desconto. Garantia de 7 dias com reembolso integral." },
];

const CheckItem = ({ children, small }) => (
  <div className={`lvCheckItem${small ? " lvCheckItemSmall" : ""}`}>
    <span className="lvCheckIcon">✓</span>
    <span className="lvCheckText">{children}</span>
  </div>
);

// Avião de papel (Material "send") — o botão de enviar do demo do WhatsApp.
const SendIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M3.4 20.4l17.45-7.48a1 1 0 000-1.84L3.4 3.6a.993.993 0 00-1.39.91L2 9.12c0 .5.37.93.87.99L17 12 2.87 13.88c-.5.07-.87.5-.87 1l.01 4.61c0 .71.73 1.2 1.39.91z" />
  </svg>
);

export default function LandingLesvita() {
  const navigate = useNavigate();

  /* O modo de CTA nasce em APP e só troca se o painel disser outra coisa —
     assim os botões nunca ficam inertes esperando a rede. */
  const cta = ctaDaLanding(useModoDoCta());

  /* Em modo APP o clique resolve a loja pelo aparelho. O `href` do link segue
     sendo `/baixar`, que é o caminho de quem abre em nova aba ou está sem JS. */
  const aoTocarNoCta = (origem) => (evento) => {
    if (!cta.paraApp) return;
    evento.preventDefault();
    abrirApp({ destino: "login", origem });
  };
  const [st, setSt] = useState({ shown: 0, typing: false, notif: false, faded: false });
  const [menuOpen, setMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState([false, false, false, false, false]);
  const chatRef = useRef(null);
  const timersRef = useRef([]);

  useEffect(() => {
    const startLoop = () => {
      timersRef.current = TIMELINE.map(([t, patch]) =>
        setTimeout(() => setSt((s) => ({ ...s, ...patch })), t)
      );
      timersRef.current.push(setTimeout(startLoop, LOOP_MS));
    };
    startLoop();
    return () => timersRef.current.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    const el = chatRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [st.shown, st.typing]);

  return (
    <div className="lvLanding">
      <Seo {...seo.home} />

      {/* ── Header pill fixo ── */}
      <header className="lvHeader">
        <a href="/" className="lvHeaderBrand">
          Les<span className="lvBrandVita">Vita</span>
        </a>
        <div className="lvHeaderActions">
          {/* Login: quem JÁ tem conta entra pelo app. Um clique só — o util
              detecta o sistema, abre o app instalado ou manda para a loja
              certa. Nada de perguntar "qual loja?" ao usuário. */}
          <button onClick={() => navigate("/entrar")} className="lvHeaderLogin lvNavLink">
            Entrar
          </button>
          <a
            href={cta.href}
            onClick={aoTocarNoCta("landing_header")}
            className="lvHeaderCta lvCtaHover"
          >
            {cta.rotulo}
          </a>
          <button onClick={() => setMenuOpen((v) => !v)} aria-label="Menu" className="lvBurger">
            <span className="lvBurgerLine" />
            <span className="lvBurgerLine" />
          </button>
        </div>
      </header>
      {menuOpen && (
        <nav className="lvMenu">
          {[["#ferramentas", "O que o VITA faz"], ["#como-funciona", "Como funciona"], ["#plano", "Plano"], ["#faq", "Perguntas frequentes"]].map(([href, label]) => (
            <a key={href} onClick={() => setMenuOpen(false)} href={href} className="lvMenuLink lvNavLink">{label}</a>
          ))}
          <button onClick={() => { setMenuOpen(false); navigate("/entrar"); }} className="lvMenuLogin lvNavLink">
            Já tenho conta — entrar no app
          </button>
        </nav>
      )}

      {/* ── Hero ── */}
      <section className="lvHero">
        <div className="lvHeroGlow" />
        <div className="lvHeroBadge">
          <span className="lvHeroBadgeDot" />
          Seu personal de IA no WhatsApp
        </div>
        <h1 className="lvHeroTitle">
          Seu personal trainer e nutri de IA.{" "}
          <span className="lvGradText">Direto no seu WhatsApp.</span>
        </h1>
        <p className="lvHeroSub">
          Treino, dieta, scan de refeição e evolução — o VITA cuida de tudo antes de você pedir. No WhatsApp e no app.
        </p>
        <a
          href={cta.href}
          onClick={aoTocarNoCta("landing_hero")}
          className="lvHeroCta lvCtaHover"
        >
          {cta.rotulo}
        </a>

        {/* ── Celular animado ── */}
        <div className="lvPhoneWrap">
          <div className="lvPhoneGlow" />
          <div className="lvPhoneFrame">
            <div className="lvPhoneScreen">
              <div className="lvPhoneNotch" />
              <div className="lvPhoneStatus">
                <span>9:41</span>
                <span className="lvPhoneStatusIcons">
                  <span className="lvPhoneBattery"><span className="lvPhoneBatteryFill" /></span>
                </span>
              </div>
              {st.notif && (
                <div className="lvNotif">
                  <img src={ICONE} alt="LesVita" className="lvNotifIcon" />
                  <span className="lvNotifBody">
                    <span className="lvNotifTitle">LesVita <span className="lvNotifTime">· agora</span></span>
                    <span className="lvNotifText">VITA registrou seu treino de Peito e Tríceps — +150 XP, ofensiva de 12 dias mantida 🔥 ✓</span>
                  </span>
                </div>
              )}
              <div className="lvChatHeader">
                <span className="lvChatBack">‹</span>
                <img src={AVATAR} alt="VITA" className="lvChatAvatarLg" />
                <span className="lvChatIdentity">
                  <span className="lvChatName">
                    VITA <span className="lvChatVerified">✔</span>
                  </span>
                  <span className="lvChatStatus">Seu personal de IA · online</span>
                </span>
              </div>
              <div ref={chatRef} className="lvChatScroll">
                <div className={`lvChatFade${st.faded ? " lvChatFadeOut" : ""}`}>
                  <div className="lvChatList">
                    {CHAT_ITEMS.slice(0, st.shown)}
                    {st.typing && (
                      <div className="lvTyping">
                        {AV}
                        <span className="lvTypingBubble">
                          <span className="lvTypingDot" />
                          <span className="lvTypingDot" />
                          <span className="lvTypingDot" />
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="lvComposer">
                <span className="lvComposerInput">Mensagem</span>
                <span className="lvComposerSend" aria-label="Enviar"><SendIcon /></span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Marquee ── */}
        <div className="lvMarquee">
          <div className="lvMarqueeTrack">
            {[0, 1].map((k) => (
              <React.Fragment key={k}>
                <span className="lvMarqueeItem">🛡 Garantia de 7 dias</span><span className="lvMarqueeSep">·</span>
                <span className="lvMarqueeItem">🔒 Seus dados protegidos (LGPD)</span><span className="lvMarqueeSep">·</span>
                <span className="lvMarqueeItem">⚡ IA de última geração</span><span className="lvMarqueeSep">·</span>
                <span className="lvMarqueeItem">📈 Evolução baseada em ciência</span><span className="lvMarqueeSep">·</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ── Ferramentas ── */}
      <section id="ferramentas" className="lvSection">
        <span className="lvEyebrow">Ferramentas</span>
        <h2 className="lvH2">O que o VITA faz por você</h2>
        <div className="lvToolGrid">
          {FERRAMENTAS.map((f) => (
            <div key={f.t} className={`lvToolCard${f.d ? " lvToolCardHi" : ""}`}>
              <span className="lvToolIcon">{f.svg}</span>
              <strong className="lvToolTitle">{f.t}</strong>
              <span className="lvToolText">{f.s}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Como funciona ── */}
      <section id="como-funciona" className="lvSectionMid lvSectionBand">
        <span className="lvEyebrow">Como funciona</span>
        <h2 className="lvH2">Três passos e o VITA assume</h2>
        <div className="lvStepGrid">
          {PASSOS.map((p) => (
            <div key={p.n} className="lvStepCard">
              <span className="lvStepNum">{p.n}</span>
              <strong className="lvStepTitle">{p.t}</strong>
              <span className="lvStepText">{p.s}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Plano ── */}
      <section id="plano" className="lvSection lvPlanSection">
        <div className="lvPlanGlow" />
        <span className="lvOfferBadge">⭐ OFERTA ESPECIAL</span>
        <h2 className="lvPlanTitle">
          Um plano completo para <span className="lvGradText">transformar seu corpo</span>
        </h2>

        <div className="lvPlanCard">
          <div className="lvTagRow">
            <span className="lvTagDark"><span className="lvTagDot" />PLANO ÚNICO</span>
            <span className="lvTagDiscount">50% DE DESCONTO</span>
          </div>
          <div className="lvPriceBlock">
            <span className="lvPriceFrom">De R$ 59,90 por</span>
            <div className="lvPriceRow">
              <span className="lvPriceValue">R$ 29,90</span>
              <span className="lvPriceUnit">/mês</span>
            </div>
            <div className="lvPriceMeta">
              <span className="lvPriceNote">Plano anual em 12x de R$ 29,90</span>
              <span className="lvSavings">Economia de 50%</span>
            </div>
          </div>
          {cta.paraApp ? (
            <a
              href={cta.href}
              onClick={aoTocarNoCta("landing_plano")}
              className="lvPlanCta lvCtaHover"
            >
              {cta.rotuloDoPlano}
            </a>
          ) : (
            <Link to="/assinar" className="lvPlanCta lvCtaHover">{cta.rotuloDoPlano}</Link>
          )}
          <div className="lvPlanTrust">🛡 Compra segura · ✓ 7 dias de garantia · 💬 Suporte humano</div>
          <div className="lvIncluso">
            <div className="lvInclusoHead">
              <span className="lvInclusoLabel">O QUE ESTÁ INCLUSO</span>
              <span className="lvInclusoTag">Tudo totalmente ilimitado</span>
            </div>
            <div className="lvInclusoList">
              {INCLUSO.map((item, i) => <CheckItem key={i}>{item}</CheckItem>)}
              {moreOpen && INCLUSO_EXTRA.map((item, i) => <CheckItem key={`x${i}`}>{item}</CheckItem>)}
              <button onClick={() => setMoreOpen((v) => !v)} className="lvMoreBtn">
                {moreOpen ? "– Mostrar menos" : "+ E muito mais"}
              </button>
            </div>
          </div>
        </div>

        {/* Card free murcho, bem abaixo */}
        <div className="lvFreeCard">
          <span className="lvFreeTitle">Ou use grátis, com limites</span>
          <div className="lvFreeList">
            <span>– Sem VITA no WhatsApp</span>
            <span>– VITA no app até 20 mensagens</span>
            <span>– 1 scan de refeição por dia</span>
            <span>– 1 registro de treino por dia</span>
            <span>– 1 análise corporal por mês</span>
            <span>– XP normal (x1) · 1 congelamento por semana</span>
          </div>
          <Link to="/baixar" className="lvFreeCta">Baixar app SEM VITA</Link>
          <div className="lvFreeStores"><span>App Store</span><span>·</span><span>Google Play</span></div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="lvSectionMid">
        <span className="lvEyebrow">FAQ</span>
        <h2 className="lvH2 lvH2Faq">Perguntas frequentes</h2>
        <div className="lvFaqList">
          {FAQ.map((f, i) => (
            <div key={i} className="lvFaqItem">
              <button
                onClick={() => setFaqOpen((arr) => arr.map((v, j) => (j === i ? !v : v)))}
                className="lvFaqQ"
              >
                {f.q}
                <span className="lvFaqSign">{faqOpen[i] ? "–" : "+"}</span>
              </button>
              {faqOpen[i] && <div className="lvFaqA">{f.a}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="lvFooter">
        <span className="lvFooterBrand">
          Les<span className="lvBrandVita">Vita</span>
        </span>
        <span className="lvFooterTag">LesVita, muito mais que avaliação.</span>
        <div className="lvFooterLinks">
          <Link to="/politicas">Termos de Uso</Link>
          <Link to="/politicas">Política de Privacidade</Link>
          <Link to="/suporte">Suporte</Link>
        </div>
        <span className="lvFooterLegal">LesVita Saúde e Tecnologia · CNPJ 61.397.301/0001-95</span>
      </footer>
    </div>
  );
}
